import { 
  Package, 
  Users, 
  Factory, 
  Truck, 
  Landmark, 
  ArrowLeftRight, 
  FileText,
  TrendingDown,
  TrendingUp,
  Minus,
  Layers,
  CircleDollarSign
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';

/**
 * ComponentBreakdownTable - Professional cost breakdown comparison table
 * 
 * Features:
 * - Lucide icons for each component type
 * - Color-coded delta column
 * - Sticky header row
 * - Bold total row at bottom
 */
export default function ComponentBreakdownTable({ 
  data, 
  baselineScenario, 
  comparisonScenario 
}) {
  if (!data || !data.component_impacts) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-gray-500 text-center">No comparison data available. Please select two scenarios to compare.</p>
        </CardContent>
      </Card>
    );
  }

  const { component_impacts, summary } = data;

  // Component type configuration with icons
  const componentConfig = {
    base_material: { label: 'Material', icon: Package, order: 1 },
    material_index: { label: 'Material Index', icon: Package, order: 2 },
    labor_index: { label: 'Labor', icon: Users, order: 3 },
    overhead: { label: 'Overhead', icon: Factory, order: 4 },
    logistics: { label: 'Logistics', icon: Truck, order: 5 },
    tariff: { label: 'Tariff', icon: Landmark, order: 6 },
    fx_impact: { label: 'FX Impact', icon: ArrowLeftRight, order: 7 },
    surcharge: { label: 'Surcharges', icon: CircleDollarSign, order: 8 },
    other: { label: 'Other', icon: FileText, order: 9 },
  };

  // Sort components by order
  const sortedComponents = [...component_impacts].sort((a, b) => {
    const orderA = componentConfig[a.component_type]?.order || 99;
    const orderB = componentConfig[b.component_type]?.order || 99;
    return orderA - orderB;
  });

  const formatCurrency = (cents) => {
    if (cents === null || cents === undefined) return '$0.00';
    return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDelta = (cents) => {
    if (cents === null || cents === undefined) return '$0.00';
    const sign = cents > 0 ? '+' : '';
    return `${sign}$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return '0.0%';
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  const getRowStyles = (deltaCents) => {
    if (deltaCents < 0) {
      return {
        bg: 'bg-green-50',
        text: 'text-green-700',
        icon: TrendingDown,
        iconClass: 'text-green-600',
      };
    } else if (deltaCents > 0) {
      return {
        bg: 'bg-red-50',
        text: 'text-red-700',
        icon: TrendingUp,
        iconClass: 'text-red-600',
      };
    }
    return {
      bg: 'bg-gray-50',
      text: 'text-gray-600',
      icon: Minus,
      iconClass: 'text-gray-400',
    };
  };

  // Calculate totals
  const totals = {
    baseline: component_impacts.reduce((sum, c) => sum + (c.baseline_amount_cents || c.baseline_cents || 0), 0),
    comparison: component_impacts.reduce((sum, c) => sum + (c.current_amount_cents || c.current_cents || 0), 0),
    delta: summary?.net_delta_cents || component_impacts.reduce((sum, c) => sum + (c.delta_cents || 0), 0),
  };
  totals.deltaPercent = totals.baseline !== 0 ? (totals.delta / totals.baseline) * 100 : 0;

  return (
    <Card className="border-gray-200 shadow-sm">
      <CardHeader className="pb-4">
        <CardTitle className="text-xl font-semibold text-gray-800 flex items-center gap-2">
          <Layers className="h-5 w-5 text-blue-600" />
          Component-Level Cost Breakdown
        </CardTitle>
        <CardDescription>
          Detailed comparison by cost driver between{' '}
          <span className="font-medium">{baselineScenario?.name || 'Baseline'}</span> and{' '}
          <span className="font-medium">{comparisonScenario?.name || 'Comparison'}</span>
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-100 border-y border-gray-200">
                <th className="text-left px-6 py-3 text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  Component
                </th>
                <th className="text-right px-6 py-3 text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  {baselineScenario?.name || 'Baseline'}
                  <Badge variant="outline" className="ml-2 text-xs">Base</Badge>
                </th>
                <th className="text-right px-6 py-3 text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  {comparisonScenario?.name || 'Comparison'}
                </th>
                <th className="text-right px-6 py-3 text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  Delta
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sortedComponents.map((component, index) => {
                const config = componentConfig[component.component_type] || { 
                  label: component.component_type, 
                  icon: FileText 
                };
                const Icon = config.icon;
                const rowStyles = getRowStyles(component.delta_cents);
                const DeltaIcon = rowStyles.icon;

                return (
                  <tr key={component.component_type} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-gray-100 rounded-md">
                          <Icon className="h-4 w-4 text-gray-600" />
                        </div>
                        <span className="font-medium text-gray-900">{config.label}</span>
                      </div>
                    </td>
                    <td className="text-right px-6 py-4 font-mono text-gray-700">
                      {formatCurrency(component.baseline_amount_cents || component.baseline_cents)}
                    </td>
                    <td className="text-right px-6 py-4 font-mono text-gray-700">
                      {formatCurrency(component.current_amount_cents || component.current_cents)}
                    </td>
                    <td className="text-right px-6 py-4">
                      <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-md ${rowStyles.bg}`}>
                        <DeltaIcon className={`h-4 w-4 ${rowStyles.iconClass}`} />
                        <span className={`font-mono font-medium ${rowStyles.text}`}>
                          {formatDelta(component.delta_cents)}
                        </span>
                        <span className={`text-sm ${rowStyles.text}`}>
                          ({formatPercent(component.delta_percent)})
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {/* Total Row */}
            <tfoot>
              <tr className="bg-gray-800 text-white">
                <td className="px-6 py-4">
                  <span className="font-bold text-lg">TOTAL</span>
                </td>
                <td className="text-right px-6 py-4 font-mono font-bold text-lg">
                  {formatCurrency(totals.baseline)}
                </td>
                <td className="text-right px-6 py-4 font-mono font-bold text-lg">
                  {formatCurrency(totals.comparison)}
                </td>
                <td className="text-right px-6 py-4">
                  <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-md ${
                    totals.delta < 0 ? 'bg-green-600' : totals.delta > 0 ? 'bg-red-600' : 'bg-gray-600'
                  }`}>
                    {totals.delta < 0 ? (
                      <TrendingDown className="h-4 w-4" />
                    ) : totals.delta > 0 ? (
                      <TrendingUp className="h-4 w-4" />
                    ) : (
                      <Minus className="h-4 w-4" />
                    )}
                    <span className="font-mono font-bold">
                      {formatDelta(totals.delta)}
                    </span>
                    <span className="text-sm">
                      ({formatPercent(totals.deltaPercent)})
                    </span>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
